import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators'
import { logger } from '@/utils/logger'
import { buildCookie } from '@/modules/cdn/cookie'

import { ApiTaCm010 } from '~/endpoints/ApiTaCm010'
import { ApiTaCt100 } from '~/endpoints/ApiTaCt100'
import { ApiTaRb010 } from '~/endpoints/ApiTaRb010'
import { ApiTaSf060 } from '~/endpoints/ApiTaSf060'

// State types
interface ContractInfo {
  currentCourseId: string
  contractInfo: {
    courseId: string
    planId: string
    levelId: string
    yyMm: {
      materialYyMm: number
      subjectId: string
      isDeliveried: number
      logicalYear: number
    }[]
  }[]
  isContractInfo: number
}
const getEmptyContractInfo = (): ContractInfo => ({
  currentCourseId: '',
  contractInfo: [
    {
      courseId: '',
      planId: '',
      levelId: '',
      yyMm: [
        {
          materialYyMm: 0,
          subjectId: '',
          isDeliveried: 0,
          logicalYear: 0,
        },
      ],
    },
  ],
  isContractInfo: 0,
})

interface AkamaiToken {
  akamaiToken: string
}
const getEmptyAkamaiToken = (): AkamaiToken => ({
  akamaiToken: '',
})

interface LabCategory {
  labCategory: {
    labCategoryId: string
    labCategoryDispName: string
    labPanelArray: {
      labPanelId: string
      labPanelDispName: string
      imageUrl: string
      contentUrl: string
      description: string
    }[]
  }[]
}
const getEmptyLabCategory = (): LabCategory => ({
  labCategory: [],
})

interface ZproLink {
  url: string
}
const getEmptyZproLink = (): ZproLink => ({
  url: '',
})

// State action parameter
interface ActionParamsLabCategory {
  coursePlan: string[]
}

interface ActionParamsZproLink {
  grade: number
}

@Module({
  stateFactory: true,
  namespaced: true,
  name: 'apis/Lab',
})
export default class Lab extends VuexModule {
  // States
  private contractInfo_: ContractInfo = getEmptyContractInfo()

  private akamaiToken_: AkamaiToken = getEmptyAkamaiToken()

  private labCategory_: LabCategory = getEmptyLabCategory()

  private zproLink_: ZproLink = getEmptyZproLink()

  // Getters
  get contractInfo(): ContractInfo {
    return this.contractInfo_
  }

  get akamaiCookie(): (expiresAt?: string) => string {
    return (expiresAt?: string) => buildCookie(this.akamaiToken_.akamaiToken, expiresAt)
  }

  get labCategory(): LabCategory {
    return this.labCategory_
  }

  get zproLink(): ZproLink {
    return this.zproLink_
  }

  get hasValidTCourseContract(): boolean {
    const isCurrentTCourse = this.contractInfo_.currentCourseId.slice(0, 1) === 'T'
    const isValid = this.contractInfo_.isContractInfo === 1
    return isCurrentTCourse && isValid
  }

  get hasValidNCourseContract(): boolean {
    const isCurrentNCourse = this.contractInfo_.currentCourseId.slice(0, 1) === 'N'
    const isValid = this.contractInfo_.isContractInfo === 1
    return isCurrentNCourse && isValid
  }

  // Mutations
  @Mutation
  updateContractInfo(value: ContractInfo) {
    this.contractInfo_ = value
  }

  @Mutation
  updateAkamaiToken(value: AkamaiToken) {
    this.akamaiToken_ = value
  }

  @Mutation
  updateLabCategory(value: LabCategory) {
    this.labCategory_ = value
  }

  @Mutation
  updateZproLink(values: ZproLink) {
    this.zproLink_ = values
  }

  // Actions
  @Action
  clear() {
    this.context.commit('updateContractInfo', getEmptyContractInfo())
    this.context.commit('updateAkamaiToken', getEmptyAkamaiToken())
    this.context.commit('updateLabCategory', getEmptyLabCategory())
    this.context.commit('updateZproLink', getEmptyZproLink())
  }

  @Action
  async getContractInfo() {
    logger.trace('in: getContractInfo')
    const api = new ApiTaCm010.Api()

    // API: ApiTaCm010
    const result = await api.get<ContractInfo | null>(
      { query: {} },
      // success
      (data, _) => {
        return {
          currentCourseId: data.currentCourseId,
          contractInfo: data.contractInfo.map(elem => {
            return {
              courseId: elem.courseId,
              planId: elem.planId,
              levelId: elem.levelId,
              yyMm: elem.yyMm.map(elem => {
                return {
                  materialYyMm: elem.materialYyMm,
                  subjectId: elem.subjectId,
                  isDeliveried: elem.isDeliveried,
                  logicalYear: elem.logicalYear,
                }
              }),
            }
          }),
          isContractInfo: data.isContractInfo,
        }
      },
      // Error
      (data, status) => {
        logger.error(status, data)
        return null
      }
    )
    if (result === null) {
      return
    }
    this.context.commit('updateContractInfo', result)
  }

  @Action
  async getAkamaiToken() {
    logger.trace('in: getAkamaiToken')
    const api = new ApiTaCt100.Api()

    // API: ApiTaCt100
    const result = await api.get<AkamaiToken | null>(
      {},
      // success
      (data, _) => {
        return {
          akamaiToken: data.cmsToken,
        }
      },
      // Error
      (data, status) => {
        logger.error(status, data)
        return null
      }
    )
    if (result === null) {
      return
    }
    this.context.commit('updateAkamaiToken', result)
  }

  @Action
  async getLabCategory(param: ActionParamsLabCategory) {
    logger.trace('in: getLabCategory')
    const api = new ApiTaRb010.Api()

    // API: ApiTaRb010
    const result = await api.get<LabCategory | null>(
      {
        query: {
          coursePlan: param.coursePlan,
        },
      },
      // success
      (data, _) => {
        return {
          labCategory: data.labCategory.map(elem => {
            return {
              labCategoryId: elem.labCategoryId,
              labCategoryDispName: elem.labCategoryDispName,
              labPanelArray: elem.labPanelArray.map(elem => {
                return {
                  labPanelId: elem.labPanelId,
                  labPanelDispName: elem.labPanelDispName,
                  imageUrl: elem.imageUrl,
                  contentUrl: elem.contentUrl,
                  description: elem.description,
                }
              }),
            }
          }),
        }
      },
      // Error
      (data, status) => {
        logger.error(status, data)
        return null
      }
    )
    if (result === null) {
      return
    }
    this.context.commit('updateLabCategory', result)
  }

  @Action
  async getZproLink(param: ActionParamsZproLink) {
    logger.trace('in: getZproLink')
    const api = new ApiTaSf060.Api()

    // API: ApiTaRb010
    const result = await api.get<ZproLink | null>(
      {
        query: {
          grade: param.grade,
        },
      },
      // success
      (data, _) => {
        return {
          url: data.url,
        }
      },
      // Error
      (_, __) => {
        return null
      }
    )
    if (result === null) {
      return
    }
    this.context.commit('updateZproLink', result)
  }
}
